
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useEnhancedSearch, useSearchSuggestions } from '@/hooks/useEnhancedSearch';
import { useDisciplines } from '@/hooks/useDisciplines';
import { Search, Filter, BookOpen, Clock, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

const EnhancedSearch: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    disciplineId: '',
    status: 'published',
    nodeType: '',
    tags: [] as string[],
  });
  const [showFilters, setShowFilters] = useState(false);

  const { data: disciplines } = useDisciplines();
  const { data: suggestions } = useSearchSuggestions(searchQuery);
  const { data: searchResults, isLoading } = useEnhancedSearch({
    query: searchQuery,
    ...filters,
  });

  const nodeTypes = [
    { value: 'article', label: 'Articles' },
    { value: 'lesson', label: 'Lessons' },
    { value: 'concept', label: 'Concepts' },
    { value: 'reference', label: 'References' },
  ];

  const clearFilters = () => {
    setFilters({
      disciplineId: '',
      status: 'published',
      nodeType: '',
      tags: [],
    });
  };

  const hasActiveFilters = filters.disciplineId || filters.nodeType || filters.tags.length > 0;

  return (
    <div className="space-y-6">
      {/* Search Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Enhanced Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="relative">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for concepts, topics, or content..."
                className="pr-12"
              />
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>

            {/* Search Suggestions */}
            {suggestions && suggestions.length > 0 && searchQuery.length >= 2 && (
              <div className="border rounded-lg p-2 bg-muted/50">
                <div className="text-xs text-black mb-2">Suggestions:</div>
                <div className="flex flex-wrap gap-1">
                  {suggestions.slice(0, 6).map((suggestion, index) => (
                    <Button
                      key={index}
                      variant="ghost"
                      size="sm"
                      className="text-xs h-6"
                      onClick={() => setSearchQuery(suggestion.suggestion)}
                    >
                      {suggestion.suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Filters */}
            {showFilters && (
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Filters</h3>
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Clear All
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium">Discipline</label>
                    <Select
                      value={filters.disciplineId}
                      onValueChange={(value) => setFilters(prev => ({ ...prev, disciplineId: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any discipline" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Any discipline</SelectItem>
                        {disciplines?.map((discipline) => (
                          <SelectItem key={discipline.id} value={discipline.id}>
                            {discipline.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Content Type</label>
                    <Select
                      value={filters.nodeType}
                      onValueChange={(value) => setFilters(prev => ({ ...prev, nodeType: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Any type</SelectItem>
                        {nodeTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end">
                    <div className="flex items-center gap-2 text-sm">
                      <span>Active filters:</span>
                      <Badge variant="secondary">
                        {hasActiveFilters ? 'ON' : 'OFF'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      <Card>
        <CardHeader>
          <CardTitle>
            Search Results
            {searchResults && (
              <span className="text-sm font-normal text-black ml-2">
                ({searchResults.length} found)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="p-4 border rounded-lg">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2 mb-2" />
                  <Skeleton className="h-3 w-full" />
                </div>
              ))}
            </div>
          ) : searchResults && searchResults.length > 0 ? (
            <div className="space-y-4">
              {searchResults.map((result) => (
                <div key={result.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <Link 
                        to={`/node/${result.id}`}
                        className="font-medium hover:text-primary transition-colors"
                      >
                        {result.title}
                      </Link>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {result.node_type}
                        </Badge>
                        {result.discipline_name && (
                          <Badge variant="secondary" className="text-xs">
                            {result.discipline_name}
                          </Badge>
                        )}
                        {result.author_full_name && (
                          <span className="text-xs text-black">
                            by {result.author_full_name}
                          </span>
                        )}
                      </div>
                    </div>
                    {result.search_rank && (
                      <div className="flex items-center gap-1 text-xs text-black">
                        <Star className="h-3 w-3" />
                        {(result.search_rank * 100).toFixed(0)}%
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-black">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(result.created_at).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen className="h-3 w-3" />
                      {result.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : searchQuery || hasActiveFilters ? (
            <div className="text-center py-8 text-black">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No results found</p>
              <p className="text-sm">Try adjusting your search terms or filters</p>
            </div>
          ) : (
            <div className="text-center py-8 text-black">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Enter a search term to find content</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedSearch;
